import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { ProjectFormModal } from 'features/projects/components/ProjectFormModal';
import { useProjectActions } from 'features/projects/hooks/useProjectActions';
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import { HeroActionButton } from 'shared/components/HeroContainer';
import { ROUTE_PATHS } from 'shared/constants/routes';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  ClientDetailPageRoot,
  ClientKpiCard,
  ClientKpiDot,
  ClientKpiGrid,
  ClientKpiLabel,
  ClientKpiLabelRow,
  ClientKpiLabelSub,
  ClientKpiValue,
  DETAIL_ACTIONS_DIVIDER_SX,
  DETAIL_ACTIONS_MENU_ITEM_ACTIVE_SX,
  DETAIL_ACTIONS_MENU_ITEM_SX,
  DETAIL_ACTIONS_MENU_LIST_SX,
  DETAIL_ACTIONS_MENU_PAPER_SX,
  DetailActionsDeleteMenuItem,
  DetailHero,
  DetailHeroActionsRow,
  DetailHeroBlobBottomLeft,
  DetailHeroBlobMid,
  DetailHeroBlobTopRight,
  DetailHeroBreadcrumbLink,
  DetailHeroBreadcrumbs,
  DetailHeroBreadcrumbSeparatorIcon,
  DetailHeroContent,
  DetailHeroDotTexture,
  DetailHeroNameColumn,
  DetailHeroPill,
  DetailHeroPillDot,
  DetailHeroPillsRow,
  DetailHeroSubtitle,
  DetailHeroTitle,
  DetailHeroTitleRow,
  HeroGlassIconButton,
  LoadingContainer,
  NOT_FOUND_BACK_LINK_SX,
  PillTab,
  PillTabCount,
  PillTabsWrapper,
} from '../clients.styles';
import { useClientActions } from '../hooks/useClientActions';

import { ClientFormModal } from './ClientFormModal';
import { ClientMembersTab } from './ClientMembersTab';
import { ClientProjectsTab, formatTrackedHM } from './ClientProjectsTab';

const TABS = ['Projects', 'Members'] as const;

const ClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canEditClient, canDeleteClient } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(null);

  // Same data source, mutations, and dialogs the Clients list page uses — see
  // useClientActions — so Edit/Archive/Delete behave identically from either page.
  const {
    clients,
    isLoading,
    getClient,
    clientModal,
    openEditModal,
    closeModal,
    handleModalSubmit,
    createMutation,
    updateMutation,
    statusDialog,
    confirmStatusChange,
    cancelStatusChange,
    deleteDialog,
    requestDelete,
    confirmDelete,
    cancelDelete,
    deleteMutation,
  } = useClientActions();

  const client = id ? getClient(id) : undefined;

  // Same project data source, create modal, and mutations as /projects — used here for
  // both the hero's Projects/Billing Hours KPI tiles and the "Add Project" action; the
  // Projects tab below calls this hook itself too.
  const {
    projects: allProjects,
    projectModal,
    openCreateModal: openCreateProjectModal,
    closeModal: closeProjectModal,
    handleModalSubmit: handleProjectModalSubmit,
    createMutation: createProjectMutation,
    updateMutation: updateProjectMutation,
  } = useProjectActions();
  const clientProjects = useMemo(
    () => (client ? allProjects.filter((p) => p.clientId === client.id) : []),
    [allProjects, client],
  );
  const activeProjectCount = clientProjects.filter((p) => p.status === 'Active').length;
  const totalBillingHours = clientProjects.reduce((sum, p) => sum + (p.trackedHours ?? 0), 0);

  const closeMoreMenu = () => setMoreMenuAnchor(null);

  const handleEditClientClick = () => {
    closeMoreMenu();
    if (id) openEditModal(id);
  };


  const handleDeleteClientClick = () => {
    closeMoreMenu();
    if (id) requestDelete(id);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  if (!client) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} mt={8}>
        <Typography variant="body2" color="text.secondary">
          {`Client "${id}" was not found.`}
        </Typography>
        <Typography
          component="button"
          onClick={() => navigate(ROUTE_PATHS.CLIENTS)}
          sx={NOT_FOUND_BACK_LINK_SX}
        >
          Back to Clients
        </Typography>
      </Box>
    );
  }

  return (
    <ClientDetailPageRoot>
      <DetailHero>
        <DetailHeroBlobTopRight aria-hidden />
        <DetailHeroBlobMid aria-hidden />
        <DetailHeroBlobBottomLeft aria-hidden />
        <DetailHeroDotTexture aria-hidden />
        <DetailHeroContent>
          <DetailHeroBreadcrumbs separator={<DetailHeroBreadcrumbSeparatorIcon />}>
            <DetailHeroBreadcrumbLink underline="hover" onClick={() => navigate(ROUTE_PATHS.CLIENTS)}>
              Clients
            </DetailHeroBreadcrumbLink>
            <Typography color="#fff" fontSize={14} fontWeight={500}>
              {client.name}
            </Typography>
          </DetailHeroBreadcrumbs>

          <DetailHeroTitleRow>
            <DetailHeroNameColumn>
              <DetailHeroTitle>{client.name}</DetailHeroTitle>
              {client.email && <DetailHeroSubtitle>{client.email}</DetailHeroSubtitle>}
              <DetailHeroPillsRow>
                <DetailHeroPill muted={client.status !== 'Active'}>
                  <DetailHeroPillDot dotStatus={client.status} />
                  {client.status === 'Active' ? 'Active Client' : client.status === 'OnHold' ? 'On Hold' : 'Inactive Client'}
                </DetailHeroPill>
              </DetailHeroPillsRow>
            </DetailHeroNameColumn>

            <DetailHeroActionsRow>
              <HeroActionButton type="button" onClick={openCreateProjectModal}>
                <Plus size={16} />
                Add Project
              </HeroActionButton>
              {(canEditClient || canDeleteClient) && (
                <HeroGlassIconButton
                  type="button"
                  aria-label="More actions"
                  onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                >
                  <MoreHorizontal size={18} />
                </HeroGlassIconButton>
              )}
            </DetailHeroActionsRow>
          </DetailHeroTitleRow>

          <ClientKpiGrid>
            <ClientKpiCard>
              <ClientKpiLabelRow>
                <ClientKpiDot dotcolor="#A5B4FC" />
                <ClientKpiLabel>
                  Projects <ClientKpiLabelSub>· {activeProjectCount} active</ClientKpiLabelSub>
                </ClientKpiLabel>
              </ClientKpiLabelRow>
              <ClientKpiValue>{clientProjects.length}</ClientKpiValue>
            </ClientKpiCard>
            <ClientKpiCard>
              <ClientKpiLabelRow>
                <ClientKpiDot dotcolor="#7DD3FC" />
                <ClientKpiLabel>
                  Members <ClientKpiLabelSub>· {client.activeMappedMemberCount} active</ClientKpiLabelSub>
                </ClientKpiLabel>
              </ClientKpiLabelRow>
              <ClientKpiValue>{client.mappedMemberCount}</ClientKpiValue>
            </ClientKpiCard>
            <ClientKpiCard>
              <ClientKpiLabelRow>
                <ClientKpiDot dotcolor="#34D399" />
                <ClientKpiLabel>Billing Hours</ClientKpiLabel>
              </ClientKpiLabelRow>
              <ClientKpiValue>{formatTrackedHM(totalBillingHours * 3600)}</ClientKpiValue>
            </ClientKpiCard>
          </ClientKpiGrid>
        </DetailHeroContent>
      </DetailHero>

      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: DETAIL_ACTIONS_MENU_PAPER_SX } }}
        sx={DETAIL_ACTIONS_MENU_LIST_SX}
      >
        {canEditClient && (
          <MenuItem sx={DETAIL_ACTIONS_MENU_ITEM_ACTIVE_SX} onClick={handleEditClientClick}>
            <Pencil size={16} />
            Edit client
          </MenuItem>
        )}
        {canEditClient && canDeleteClient && <Divider sx={DETAIL_ACTIONS_DIVIDER_SX} />}
        {canDeleteClient && (
          <DetailActionsDeleteMenuItem
            sx={DETAIL_ACTIONS_MENU_ITEM_SX}
            onClick={handleDeleteClientClick}
          >
            <Trash2 size={16} />
            Delete client
          </DetailActionsDeleteMenuItem>
        )}
      </Menu>

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={() => confirmDelete(() => navigate(ROUTE_PATHS.CLIENTS))}
        onCancel={cancelDelete}
        loading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={statusDialog.open}
        title="Set Client Inactive"
        message={`Setting "${client.name}" to inactive will also set all projects under it to inactive. Are you sure you want to proceed?`}
        confirmLabel="Set Inactive"
        onConfirm={confirmStatusChange}
        onCancel={cancelStatusChange}
        loading={updateMutation.isPending}
      />

      <ClientFormModal
        open={clientModal.open}
        client={
          clientModal.clientId ? (clients.find((c) => c.id === clientModal.clientId) ?? null) : null
        }
        initialName={clientModal.initialName}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      <ProjectFormModal
        open={projectModal.open}
        project={
          projectModal.projectId
            ? (allProjects.find((p) => p.id === projectModal.projectId) ?? null)
            : null
        }
        clients={clients}
        onClose={closeProjectModal}
        onSubmit={handleProjectModalSubmit}
        isPending={createProjectMutation.isPending || updateProjectMutation.isPending}
      />

      <PillTabsWrapper>
        {TABS.map((label, index) => (
          <PillTab
            key={label}
            type="button"
            active={activeTab === index}
            onClick={() => setActiveTab(index)}
          >
            {label}
            <PillTabCount active={activeTab === index}>
              {label === 'Projects' ? clientProjects.length : client.mappedMemberCount}
            </PillTabCount>
          </PillTab>
        ))}
      </PillTabsWrapper>

      {TABS[activeTab] === 'Projects' && <ClientProjectsTab clientId={client.id} />}
      {TABS[activeTab] === 'Members' && (
        <ClientMembersTab clientId={client.id} clientName={client.name} />
      )}
    </ClientDetailPageRoot>
  );
};

export default ClientDetailPage;
